/**
 * LIGHT OR DARK, and who decides.
 *
 * THREE STATES, not two. The machine already has an answer -- every OS
 * carries a light/dark preference and the browser reports it -- so the
 * default is to obey it rather than to pick for people. An explicit
 * choice overrides that and is remembered; clearing the choice hands
 * the decision back.
 *
 * Following the system also means following it LIVE: someone whose
 * desktop flips at sunset should see this flip too, without a reload,
 * unless they have said otherwise.
 *
 * There is no stylesheet (docs/STRATA.md, 2026-09-18), so the mode is
 * not published to CSS. It is published to the DOCUMENT: the root's
 * color-scheme, which is what the browser paints its own chrome
 * (scrollbars, form controls, the default canvas) with, and the body's
 * ground and ink, so the page is right before the first component
 * mounts. Everything else reads the Palette from the theme context.
 */
import { useEffect, useState } from 'react';
import { DARK, LIGHT, type Palette } from './palette';
import { FONT } from './geometry';

export type ThemeMode = 'light' | 'dark';

/** Where an explicit choice is remembered, per browser. */
const STORAGE_KEY = 'polywog.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The palette a mode paints with. */
export function paletteFor(mode: ThemeMode): Palette {
  return mode === 'dark' ? DARK : LIGHT;
}

/**
 * The choice this browser has stored, or null when none was made.
 *
 * @returns The stored mode, or null to follow the system. Storage can
 *   throw outright (private windows, blocked site data), and a theme is
 *   not worth a crash -- an unreadable preference is no preference.
 */
export function storedMode(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

/**
 * What the machine prefers right now.
 *
 * @returns The OS preference, defaulting to light where the browser
 *   cannot say.
 */
export function systemMode(): ThemeMode {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Remember a choice, or forget it and follow the system again.
 *
 * @param mode - The chosen mode, or null to defer to the machine.
 */
export function rememberMode(mode: ThemeMode | null): void {
  try {
    if (mode === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A browser that will not store a preference still honours one for
    // this session; the document below is what actually paints.
  }
}

/**
 * Publish the mode to the document: the browser's own chrome follows
 * color-scheme, and the body carries the ground so nothing flashes
 * white under a dark palette while React mounts.
 *
 * @param mode - The active mode.
 */
export function publishMode(mode: ThemeMode): void {
  const palette = paletteFor(mode);
  const root = document.documentElement;
  root.style.colorScheme = mode;
  root.style.backgroundColor = palette.surface;
  const body = document.body;
  body.style.margin = '0';
  body.style.backgroundColor = palette.surface;
  body.style.color = palette.ink;
  body.style.fontFamily = FONT.sans;
}

/**
 * The active mode, and how to change it.
 *
 * @returns The mode in force, and a setter taking null to go back to
 *   following the machine.
 */
export function useThemeMode(): [ThemeMode, (mode: ThemeMode | null) => void] {
  const [chosen, setChosen] = useState<ThemeMode | null>(storedMode);
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  // Follow the machine LIVE while nothing has been chosen.
  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode = chosen ?? system;
  useEffect(() => publishMode(mode), [mode]);

  return [
    mode,
    (next: ThemeMode | null) => {
      rememberMode(next);
      setChosen(next);
    },
  ];
}
